import type { FunctionComponentWithChildren } from '@bonsai-components/utility-types';
import { borderRadius, semanticColors } from '@moderneinc/design-system-tokens';
import { Box } from '@mui/material';
import { useContext } from 'react';
import { ConfigurationContext } from '../../contexts/config.context';
import { useCommonBreakpoints } from '../../hooks/use-breakpoints.hooks';
import { useCapabilities } from '../../hooks/use-capabilities.hooks';
import { GlobalNavigationBar } from '../global-navigation/global-navigation-bar/global-navigation-bar.component';
import { MissingOrganizationModal } from '../global-navigation/missing-organization-modal/missing-organization-modal.component';
import { LeftNavigation } from '../navigation/left-navigation/left-navigation.component';
import { OnboardingModal } from '../onboarding/onboarding-modal.component';
import { ProfilingAdminToast } from '../profiling/profiling-admin-toast.component';
import { ProfilingBanner } from '../profiling/profiling-banner.component';
import { PersistentToastStack } from '../utilities/persistent-toast-stack/persistent-toast-stack.component';
import { UpdateApplication } from '../utilities/update-app/update-app.component';

export const Layout: FunctionComponentWithChildren = ({ children }) => {
  const { isLargeDevice } = useCommonBreakpoints();
  const { profiling } = useCapabilities();
  const { internalState } = useContext(ConfigurationContext);

  return (
    <Box
      sx={{
        backgroundColor: semanticColors.color.surface.page,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {profiling.isActive && <ProfilingBanner />}
      <PersistentToastStack>
        <ProfilingAdminToast />
        {internalState.updateRequired && <UpdateApplication />}
      </PersistentToastStack>
      <OnboardingModal />
      <MissingOrganizationModal />
      <Box
        id="moderne-app"
        sx={{
          display: 'grid',
          // `auto` lets the ModSideNav rail own its width (it animates between its
          // expanded and collapsed sizes), so the content column reclaims the space
          // when the rail collapses instead of leaving a fixed-width gap.
          gridTemplateColumns: isLargeDevice ? 'auto 1fr' : '1fr',
          gridTemplateAreas: isLargeDevice
            ? '"global-nav content"'
            : '"content"',
          flex: 1,
          overflow: 'hidden',
          px: isLargeDevice ? undefined : 0.5
        }}
      >
        {/* Left Panel - Navigation Rail (only show on desktop) */}
        {/* minHeight/overflow let the cell shrink below its content so the rail
            stays viewport-height and ModSideNav scrolls its items internally
            (keeping the header, footer, and toggle pinned) on short screens. */}
        {isLargeDevice && (
          <Box
            sx={{ gridArea: 'global-nav', minHeight: 0, overflow: 'hidden' }}
          >
            <LeftNavigation />
          </Box>
        )}

        {/* Right Panel - Global Nav + Content */}
        <Box
          sx={{
            gridArea: 'content',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            overflow: 'hidden'
          }}
        >
          {/* Global nav bar */}
          <Box sx={{ flexShrink: 0 }}>
            <GlobalNavigationBar />
          </Box>

          {/* Page content */}
          <Box
            id="moderne-content-scroll-container"
            sx={{
              backgroundColor: semanticColors.color.surface.canvas,
              flex: 1,
              overflow: 'auto',
              // Hairline on the two edges that meet the page chrome, so the
              // content panel reads as raised against it.
              borderTop: `1px solid ${semanticColors.color.border.primary}`,
              borderLeft: `1px solid ${semanticColors.color.border.primary}`,
              borderRadius: `${borderRadius.m}px 0 0 0`
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
